import type { ToolCallMessagePart, ToolCallMessagePartStatus } from '@assistant-ui/react';
import {
  activityOutcome,
  toolInputSummary,
  toolLabel,
  toolOutcome,
  type ToolOutcome,
} from './tool-presentation';
import { activityResultCount, earlierAttempts, namedConnectionStep } from './activity-summary';
import { ACTIVITY_VERBS, localizedActivityVerb } from './activity-verbs';
import { APP_COPY } from '@/i18n/catalogs';
import { resolveCatalogLocale } from '@/i18n/context';
import { citationTarget } from '@/citations/reference';

export type ActivityTraceStep = {
  id: string;
  toolName: string;
  label: string;
  pack: string;
  status?: ToolCallMessagePartStatus;
  outcome: ToolOutcome;
  /** DES1 HC-10: a failed attempt that a later call of the same thing completed. */
  earlier?: boolean;
  /** DES1 HC-12: a named database read's own failed line; otherwise the tool's generic one. */
  failedLabel?: string;
  argsText?: string;
  result?: unknown;
  isError?: boolean;
  approval?: unknown;
  interrupt?: unknown;
  /** Server supplied source references only. The client never derives these. */
  sourceIds: string[];
  sourceLinks?: Array<{ id: string; href?: string }>;
  citedSourceIds?: string[];
  /** Structural summaries used by the trace row; details remain the evidence. */
  inputSummary?: string;
  resultCount?: number | null;
  durationMs?: number;
  text?: string;
};

export type ActivityTrace = {
  id: string;
  messageId: string;
  outcome: ToolOutcome;
  steps: ActivityTraceStep[];
  sourceIds: string[];
  sourceLinks?: Array<{ id: string; href?: string }>;
  citedSourceIds?: string[];
};

type ToolTracePart = Pick<
  ToolCallMessagePart,
  'toolCallId' | 'toolName' | 'argsText' | 'result' | 'isError' | 'approval' | 'interrupt'
> & { type: 'tool-call'; status?: ToolCallMessagePartStatus };
type TracePart = ToolTracePart | { type: 'reasoning'; text?: string };

function citationParts(value: string): { kind: string; id: string; revision: string } | null {
  const target = citationTarget(value, 'https://chat.poneva.com/');
  if (!target) return null;
  const url = new URL(target.href, 'https://chat.poneva.com/');
  const [, query = ''] = url.hash.slice(1).split('?');
  const params = new URLSearchParams(query);
  const kind = params.get('canvas');
  const id = params.get('id');
  if (!kind || !id) return null;
  return { kind, id, revision: params.get('revision') ?? '' };
}

export function sourceReferenceKey(value: string | { id: string; href?: string }): string {
  const id = typeof value === 'string' ? value : value.id;
  const href = typeof value === 'string' ? undefined : value.href;
  const candidate = href ?? id;
  const parsed = citationParts(candidate);
  return parsed
    ? `citation:${parsed.kind}:${parsed.id}:${parsed.revision}`
    : href
      ? `href:${href}`
      : `id:${id}`;
}

function mergeSourceRefs(
  refs: ReadonlyArray<{ id: string; href?: string }>,
): Array<{ id: string; href?: string }> {
  const merged = new Map<string, { id: string; href?: string }>();
  for (const ref of refs) {
    const key = sourceReferenceKey(ref);
    const previous = merged.get(key);
    merged.set(key, previous ? { ...previous, href: previous.href ?? ref.href } : ref);
  }
  // A bare server source id can be paired with one valid citation URL from
  // the same result. If several kinds/revisions use that id, keep the bare
  // reference separate rather than guessing which historical target it is.
  for (const [key, ref] of [...merged]) {
    if (!key.startsWith('id:')) continue;
    const candidates = [...merged.entries()].filter(([candidateKey, candidate]) => {
      if (!candidateKey.startsWith('citation:')) return false;
      const parsed = citationParts(candidate.href ?? candidate.id);
      return parsed?.id === ref.id;
    });
    if (candidates.length !== 1) continue;
    const [candidateKey, candidate] = candidates[0];
    merged.set(candidateKey, { ...candidate, id: ref.id, href: candidate.href });
    merged.delete(key);
  }
  return [...merged.values()];
}

function sourceRefs(value: unknown): Array<{ id: string; href?: string }> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const record = value as Record<string, unknown>;
  const ids = record.sourceIds ?? record.source_ids;
  const direct = Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
  const references = [record.referenceUrl, record.reference_url, record.sourceUrl].filter(
    (url): url is string => typeof url === 'string' && url.length > 0,
  );
  const nested = Array.isArray(record.sources)
    ? record.sources.flatMap((source) => {
        if (!source || typeof source !== 'object') return [];
        const item = source as Record<string, unknown>;
        const id = typeof item.id === 'string' ? item.id : undefined;
        const href = [item.referenceUrl, item.url].find(
          (value): value is string => typeof value === 'string' && value.length > 0,
        );
        return id ? [{ id, href }] : [];
      })
    : [];
  return mergeSourceRefs([
    ...direct.map((id) => ({ id })),
    ...references.map((href) => ({ id: href, href })),
    ...nested,
  ]);
}

/** Build an auditable trace from canonical assistant-ui tool parts. */
export function activityTrace(
  messageId: string,
  parts: readonly TracePart[],
  messageSourceIds: readonly string[] = [],
  locale = 'en',
  citedSourceIds: readonly string[] = [],
): ActivityTrace {
  const earlier = earlierAttempts(parts);
  const steps = parts.map((part) => {
    if (part.type === 'reasoning') {
      return {
        id: `${messageId}:reasoning:${stepsIndex(parts, part)}`,
        toolName: 'reasoning',
        label: APP_COPY[resolveCatalogLocale(locale)].activityThinking,
        pack: '',
        outcome: 'complete' as const,
        sourceIds: [],
        sourceLinks: [],
        text: part.text,
      } satisfies ActivityTraceStep;
    }
    const toolPart = part as ToolTracePart;
    const { label, pack } = toolLabel(toolPart.toolName);
    const verb = ACTIVITY_VERBS[toolPart.toolName];
    const refs = sourceRefs(toolPart.result);
    const named = namedConnectionStep(toolPart, locale, parts);
    return {
      id: toolPart.toolCallId,
      toolName: toolPart.toolName,
      label:
        named?.done ?? (verb ? localizedActivityVerb(locale, toolPart.toolName, verb).done : label),
      ...(named ? { failedLabel: named.failed } : {}),
      pack,
      status: toolPart.status,
      outcome: toolOutcome(toolPart),
      ...(earlier.has(part) ? { earlier: true } : {}),
      argsText: toolPart.argsText,
      result: toolPart.result,
      isError: toolPart.isError,
      approval: toolPart.approval,
      interrupt: toolPart.interrupt,
      sourceIds: refs.filter((ref) => !ref.href || ref.id !== ref.href).map((ref) => ref.id),
      sourceLinks: refs,
      inputSummary: toolInputSummary(toolPart.argsText),
      resultCount: activityResultCount(toolPart.result),
    } satisfies ActivityTraceStep;
  });
  return {
    id: `${messageId}:activity`,
    messageId,
    outcome: activityOutcome(
      parts.filter(
        (part): part is ToolTracePart => part.type !== 'reasoning' && !earlier.has(part),
      ),
    ),
    steps,
    sourceIds: mergeSourceRefs([
      ...messageSourceIds.map((id) => ({ id })),
      ...steps.flatMap((step) => step.sourceIds.map((id) => ({ id }))),
    ]).map((ref) => ref.id),
    sourceLinks: mergeSourceRefs([
      ...steps.flatMap((step) => step.sourceLinks ?? []),
      ...citedSourceIds.map((href) => ({ id: href, href })),
    ]),
    citedSourceIds: mergeSourceRefs(citedSourceIds.map((id) => ({ id, href: id }))).map(
      (ref) => ref.href ?? ref.id,
    ),
  };
}

function stepsIndex(parts: readonly TracePart[], part: TracePart) {
  return parts.indexOf(part);
}
